/**
 * The ticket panel in the desktop (#1170): anchored above the taskbar, the
 * context taken from the focused window, "Meine Tickets" opened in Cortex the
 * way a bug-report notification opens it (an open Cortex window is reused).
 * Someone else's ticket (admins) opens there too: for admins the Cortex tab is
 * the admin view (#1191).
 */
import { useCallback } from 'react'
import { useWindows } from '@/hooks/useWindows'
import { reusableWindow } from '@/lib/window-identity'
import { TicketPanel } from './TicketPanel'
import { desktopSystemFor, windowLabel, type TicketContextSource } from './ticketContext'

export function DesktopTicketPanel() {
  const { windows, activeId, openWindow, focusWindow, updateWindowParams } = useWindows()

  const readContextSource = useCallback((): TicketContextSource => {
    const focused = windows.find((w) => w.id === activeId && !w.minimized)
    return {
      system: desktopSystemFor(focused?.appId),
      app: focused?.appId ?? null,
      window: focused ? windowLabel(focused.title, focused.subtitle) : null,
      route: `${window.location.pathname}${window.location.hash}`,
    }
  }, [windows, activeId])

  const openInCortex = useCallback(
    (reportId: string) => {
      const params = { tab: 'bug-reports', reportId }
      const existing = reusableWindow(windows, 'cortex', params)
      if (existing) {
        updateWindowParams(existing.id, params)
        focusWindow(existing.id)
        return
      }
      void openWindow('cortex', undefined, undefined, params)
    },
    [windows, openWindow, focusWindow, updateWindowParams],
  )

  return (
    <TicketPanel
      anchor="taskbar-right"
      system="webos"
      readContextSource={readContextSource}
      onOpenMyTickets={openInCortex}
      onOpenOthersTicket={openInCortex}
    />
  )
}
